import { Link, useParams } from 'react-router-dom';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { getMoviesByIds, getSpecialById } from '@/data/catalog';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';

export default function Special() {
  const { id = '' } = useParams();
  const topic = getSpecialById(id);

  useDocumentMeta({
    title: topic ? `${topic.title} - 影视专题_大雷影视` : '专题不存在 - 大雷影视',
    description: topic
      ? `${topic.title}：${topic.subtitle}。${topic.intro.slice(0, 70)}`
      : '未找到对应的影视专题，请返回大雷影视首页浏览其他专题合集。',
    keywords: topic ? `${topic.title},影视专题,影视合集,大雷影视,${topic.movieIds.length}部作品` : '大雷影视,影视专题',
  });

  if (!topic) {
    return (
      <div className="mx-auto w-full max-w-[1440px] px-4 py-16 md:px-8">
        <EmptyState
          title="该专题不存在"
          description="这个专题可能已下线，或者链接有误。可以回到首页，从其他精选专题继续浏览。"
          actionLabel="返回首页"
          actionPath="/"
        />
      </div>
    );
  }

  const movies = getMoviesByIds(topic.movieIds);
  const relatedTopics = topic.relatedIds
    .map((relatedId) => getSpecialById(relatedId))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={topic.cover}
            alt={`${topic.title}专题主图`}
            title={`${topic.title} 大雷影视专题`}
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/40" />
        </div>

        <div className="relative mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb items={[{ label: '影视专题' }, { label: topic.title }]} light />

          <div className="mt-6 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-bookmark-fill" />
              {topic.accentLabel}
            </span>
            <h1 className="mt-4 text-3xl font-black leading-tight tracking-tight text-background-50 md:text-5xl">
              {topic.title}
            </h1>
            <p className="mt-2 text-sm text-background-200/90 md:text-base">{topic.subtitle}</p>
            <p className="mt-5 text-sm leading-7 text-background-200/80 md:text-[15px]">{topic.intro}</p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <a
                href="#special-list"
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
              >
                查看片单
                <i className="ri-arrow-down-line" />
              </a>
              <span className="inline-flex items-center gap-1.5 text-xs text-background-300/80">
                <i className="ri-film-line text-accent-400" />
                共收录 {movies.length} 部作品
              </span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1440px] px-4 py-10 md:px-8 md:py-12">
        <div id="special-list" className="scroll-mt-24">
          <div className="flex items-center gap-2.5">
            <span className="h-5 w-1 rounded-full bg-accent-500" />
            <h2 className="text-xl font-bold tracking-tight text-foreground-950 md:text-2xl">专题片单</h2>
            <span className="mt-1 font-label text-xs uppercase tracking-[0.22em] text-foreground-400">
              Collection
            </span>
          </div>
          <p className="mt-2 text-sm text-foreground-500">按编辑推荐顺序排列，点击任意作品可查看完整资料</p>

          <div className="mt-6">
            {movies.length > 0 ? (
              <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:grid-cols-4 md:gap-x-4 lg:grid-cols-5">
                {movies.map((movie, index) => (
                  <MovieCard key={movie.id} movie={movie} rank={index + 1} />
                ))}
              </div>
            ) : (
              <EmptyState
                title="该专题暂无内容"
                description="编辑正在为这个专题补充作品，你可以先去其他专题看看，或者直接浏览最新上线。"
                actionLabel="浏览其他专题"
                actionPath="/special/special-01"
              />
            )}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="rounded-lg bg-primary-50 p-6 lg:col-span-2">
            <h3 className="flex items-center gap-2 text-base font-semibold text-primary-900">
              <i className="ri-quill-pen-line text-accent-600" />
              推荐理由
            </h3>
            <p className="mt-3 text-sm leading-7 text-primary-800/85">{topic.reason}</p>
            <p className="mt-3 text-sm leading-7 text-primary-800/85">
              专题片单会随编辑更新调整，如果你有想补充的作品或发现了资料错误，欢迎通过关于页的联系方式告诉我们。
            </p>
            <Link
              to="/about"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 text-sm font-medium text-primary-700 transition-colors hover:text-accent-600"
            >
              联系我们
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="rounded-lg border border-background-200 p-6">
            <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-950">
              <i className="ri-links-line text-accent-500" />
              相关专题
            </h3>
            <div className="mt-4 flex flex-col gap-3">
              {relatedTopics.map((item) => (
                <Link
                  key={item.id}
                  to={`/special/${item.id}`}
                  className="group flex cursor-pointer items-center gap-3 rounded-md p-1.5 transition-colors hover:bg-background-100"
                >
                  <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title}专题封面`}
                      title={`${item.title} 影视专题`}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-foreground-400">{item.subtitle.split(' · ')[0]}</p>
                  </div>
                </Link>
              ))}
            </div>
            <Link
              to="/special/special-01"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 text-xs text-foreground-400 transition-colors hover:text-primary-600"
            >
              浏览全部专题
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}